import { useEffect, useState } from 'react';
import './admin-team.styles.scss';
import { useParams } from 'react-router-dom';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamHeader from '@/components/SHARED/team-header/team-header.component';
import TeamRoster from '@/components/SHARED/team-roster/team-roster.component';
import TeamSchedule from '@/components/SHARED/team-schedule/team-schedule.component';
import RankChart from '@/components/SHARED/rank-chart/rank-chart.component';
import TeamHistory from '@/components/SHARED/team-history/team-history.component';
import TeamRivals from '@/components/SHARED/team-rivals/team-rivals.component';
import RecruitClass from '@/components/SHARED/recruit-class/recruit-class.component';
import TeamScouting from '@/components/SHARED/team-scouting/team-scouting.component';
import Modal, { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// Admin view of one team: everything a manager sees, plus hidden traits.
const AdminTeam = ({ league, teams, schedule, base, rankHistory }) => {

    const { teamId } = useParams();
    const [ team, setTeam ] = useState(undefined);
    const [ traits, setTraits ] = useState(null);
    const [ editing, setEditing ] = useState(null);
    const [ saving, setSaving ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ confirmRemove, setConfirmRemove ] = useState(false);

    useEffect(() => {
        let teamListener;
        let traitsListener;
        listenToDoc({
            'path': `leagues/${league.id}/teams/${teamId}`,
            'callback': setTeam,
            'handleListener': (unsub) => { teamListener = unsub; },
        });
        // Admin-only document (security rules block managers).
        listenToDoc({
            'path': `leagues/${league.id}/private/traits`,
            'callback': (data) => setTraits(data?.teams?.[teamId] ?? null),
            'handleListener': (unsub) => { traitsListener = unsub; },
        });
        return () => {
            if (teamListener) teamListener();
            if (traitsListener) traitsListener();
        }
    }, [league.id, teamId])

    if (team === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (team === null) return <p className='meta'>Team not found.</p>;

    const conf = league.conferences[team.conference];

    async function removeManager() {
        const res = await UseCloudFunction('leaveTeam', { leagueId: league.id, teamId });
        setConfirmRemove(false);
        if (res.error) setAlertMessage(res.error);
    }

    async function save() {
        setSaving(true);
        const res = await UseCloudFunction('updateTeam', { leagueId: league.id, teamId, ...editing });
        setSaving(false);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        setEditing(null);
    }

    const field = (key, label, props = {}) => (
        <div className='field'>
            <label htmlFor={`edit-${key}`}>{label}</label>
            <input id={`edit-${key}`} type='text' value={editing[key]} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} {...props} />
        </div>
    );
    const colorField = (key, label) => (
        <div className='field color-field'>
            <label htmlFor={`edit-${key}`}>{label}</label>
            <div className='color-row'>
                <input type='color' aria-label={`${label} picker`} value={editing[key]} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} />
                <input id={`edit-${key}`} type='text' value={editing[key]} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} />
            </div>
        </div>
    );

    return (
        <div className='admin-team'>
            <TeamHeader
                league={league}
                teams={teams}
                team={team}
                meta={`${conf?.name ?? ''} · ${team.managerId ? `Managed by ${team.managerName ?? 'a user'}` : 'Auto-managed'}`}
                extra={traits && <span className='hidden-note'>Team chemistry value {traits.chemistry}</span>}
                actions={<>
                    <button className='g-button small-btn' onClick={() => setEditing({ name: team.name, mascot: team.mascot, abbr: team.abbr, color1: team.color1, color2: team.color2, city: team.city ?? '', state: team.state ?? '' })}>
                        Edit
                    </button>
                    {team.managerId &&
                        <button className='g-button small-btn' onClick={() => setConfirmRemove(true)}>Remove manager</button>
                    }
                </>}
            />

            {rankHistory &&
                <div className='g-card'>
                    <RankChart
                        history={rankHistory}
                        teamId={teamId}
                        title={league.type === 'pro' ? 'Power ranking' : 'Ranking'}
                        top25={league.type !== 'pro'}
                        regularDays={league.season.totalDays} />
                </div>
            }

            <TeamScouting team={team} />

            <div className='g-card'>
                <h3>Roster</h3>
                <p className='meta'>Shaded columns are hidden from managers.</p>
                <TeamRoster team={team} hidden={traits} playerLink={(t, p) => `${base}/teams/${t}/players/${p}`} />
            </div>

            <div className='g-card'>
                <h3>Schedule</h3>
                <TeamSchedule
                    teamId={teamId}
                    schedule={schedule}
                    year={league.season.year}
                    teams={teams}
                    gameLink={(id) => `${base}/games/${id}`}
                    teamLink={(id) => `${base}/teams/${id}`} />
            </div>

            <RecruitClass team={team} />
            <TeamRivals team={team} teams={teams} teamLink={(id) => `${base}/teams/${id}`} />
            <TeamHistory team={team} league={league} seasonLink={(year) => `${base}/history/${year}`} />

            <Modal show={Boolean(editing)} cancel={() => setEditing(null)} text='Edit team' closeButton={true} cls='narrow'>
                {editing &&
                    <div className='modal-children edit-team'>
                        {field('name', 'School / city', { maxLength: 40 })}
                        {field('mascot', 'Mascot', { maxLength: 30 })}
                        {field('abbr', 'Abbreviation', { maxLength: 5 })}
                        {field('city', 'City', { maxLength: 40 })}
                        {field('state', 'State (e.g. IL)', { maxLength: 30 })}
                        {colorField('color1', 'Primary color')}
                        {colorField('color2', 'Secondary color')}
                        <div className='badge-preview'>
                            <TeamBadge team={editing} size='lg' /> <span className='meta'>Preview</span>
                        </div>
                        <div className='buttons'>
                            <button className='g-button' onClick={() => setEditing(null)}>Cancel</button>
                            <button className={`g-button primary ${saving ? 'disabled' : ''}`} onClick={() => save()}>
                                {saving ? <ThreeDotsLoader /> : 'Save'}
                            </button>
                        </div>
                    </div>
                }
            </Modal>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
            <ModalConfirm
                show={confirmRemove}
                cancel={() => setConfirmRemove(false)}
                message={`Remove ${team.managerName ?? 'the manager'} from ${team.name}? The team will run itself.`}
                onApprove={removeManager} />
        </div>
    )
}

export default AdminTeam
